import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { my_routes } from "../../router/all_routes";

import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { resetPassword } from "../../../api/users"; // Importer la fonction d'API pour réinitialiser le mot de passe

type PasswordField = "newPassword" | "confirmPassword";

// 1. définition du shéma Zod
const resetShema = z
  .object({
    newPassword: z.string().min(8, {
      message: "Le mot de passe doit comporter au moins 8 caractères",
    }),
    confirmPassword: z.string().min(8, {
      message: "Le mot de passe doit comporter au moins 8 caractères",
    }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: " Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
  });

//2.Inference de type des données du formulaire
type ResetFormData = z.infer<typeof resetShema>;

const ResetPassword = () => {
  const routes = my_routes;

  const loginBackgrounds = [
    "/assets/img/background_1.jpeg",
    "/assets/img/background_2.jpeg",
    "/assets/img/background_3.jpeg",
    "/assets/img/background_4.jpeg",
  ];

  const [loginBackground] = useState(() => {
    const randomIndex = Math.floor(Math.random() * loginBackgrounds.length);
    return loginBackgrounds[randomIndex];
  });

  const [passwordVisibility, setPasswordVisibility] = useState({
    newPassword: false,
    confirmPassword: false,
  });

  // Récupération du token de réinitialisation depuis les paramètres de l'URL
  const location = useLocation();
  const resetPasswordToken = new URLSearchParams(location.search).get("token");

  // Destructuration du shema pour la gestion du formulaire avec useForm
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetShema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: ResetFormData) => {
    try {
      // console.log("Données envoyées avec succes !:", data);
      // Vérification de la présence du token de réinitialisation
      if (!resetPasswordToken) {
        setError("root", {
          message: "Token de réinitialisation manquant",
        });
        return;
      }

      // Appel à l'API pour réinitialiser le mot de passe
      const response = await resetPassword(
        resetPasswordToken,
        data.newPassword,
      );
      if (!response) {
        setError("root", {
          message: "Échec de la réinitialisation du mot de passe",
        });
      } else {
        navigation(routes.login);
      }
    } catch (error: any) {
      setError("root", {
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "une erreur inattendu c'est produite veuillez ésssyez plus tard",
      });

      // console.log("Erreur: ", error);
    }
  };

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };
  const navigation = useNavigate();

  return (
    <div className="container-fuild">
      <div
        className="w-100 overflow-hidden position-relative flex-wrap d-block vh-100"
        style={{
          backgroundImage: `url(${loginBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="row"
          style={{
            backdropFilter: "blur(2.5px)",
          }}
        >
          <div className="col-lg-12 col-md-12 col-sm-12">
            <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap ">
              <div className="col-md-4 col-sm-8 mx-auto p-4">
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  <div
                    className="card shadow"
                    style={{ backgroundColor: "rgba(255,255,255,0.90)" }}
                  >
                    <div className="card-body p-4">
                      <img
                        src="/assets/img/authentication/Logo-Campus-removebg.png"
                        style={{ background: "transparent" }}
                        alt="Logo Campus UIE"
                      />

                      <div className="mb-4">
                        <h2 className="mb-2">Réinitialiser le mot de passe</h2>
                        <p className="mb-0">
                          Choisissez un nouveau mot de passe sécurisé pour votre
                          compte.
                        </p>
                      </div>

                      {errors.root && (
                        <div className="alert alert-danger" role="alert">
                          {errors.root.message}
                        </div>
                      )}

                      <div className="mb-3">
                        <label className="form-label" htmlFor="newPassword">
                          Nouveau mot de passe
                        </label>
                        <div className="pass-group">
                          <input
                            id="newPassword"
                            {...register("newPassword")}
                            type={
                              passwordVisibility.newPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control input"
                            autoComplete="new-password"
                          />
                          <span
                            className={`ti toggle-password ${
                              passwordVisibility.newPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                            }`}
                            onClick={() =>
                              togglePasswordVisibility("newPassword")
                            }
                            role="button"
                            aria-label="Afficher ou masquer le nouveau mot de passe"
                          />
                        </div>
                        {errors.newPassword && (
                          <span className="text-danger">
                            {errors.newPassword.message}
                          </span>
                        )}
                      </div>

                      <div className="mb-3">
                        <label className="form-label" htmlFor="confirmPassword">
                          Confirmer le mot de passe
                        </label>
                        <div className="pass-group">
                          <input
                            id="confirmPassword"
                            {...register("confirmPassword")}
                            type={
                              passwordVisibility.confirmPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control input"
                            autoComplete="new-password"
                          />
                          <span
                            className={`ti toggle-password ${
                              passwordVisibility.confirmPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                            }`}
                            onClick={() =>
                              togglePasswordVisibility("confirmPassword")
                            }
                            role="button"
                            aria-label="Afficher ou masquer la confirmation du mot de passe"
                          />
                        </div>
                        {errors.confirmPassword && (
                          <span className="text-danger">
                            {errors.confirmPassword.message}
                          </span>
                        )}
                      </div>

                      <div className="mb-3">
                        <button
                          type="submit"
                          disabled={isSubmitting || !resetPasswordToken}
                          className="btn btn-primary w-100"
                        >
                          {isSubmitting
                            ? "Réinitialisation en cours..."
                            : "Réinitialiser le mot de passe"}
                        </button>
                      </div>

                      <div className="text-center">
                        <h6 className="fw-normal text-dark mb-0">
                          Retour à l'{" "}
                          <Link to={routes.login} className="hover-a">
                            authentification
                          </Link>
                        </h6>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
